import React, { useState, useEffect } from 'react';
import { message, Typography, Card, Button, Alert, Divider, Row, Col } from 'antd';
import { 
    PrinterOutlined, SaveOutlined, ContainerOutlined, ProfileOutlined, FileTextOutlined, 
    InfoCircleOutlined, DesktopOutlined, ThunderboltOutlined 
} from '@ant-design/icons';

const { Title, Text } = Typography;

const SeccionImpresion = ({ config, configLoading, updateConfig }) => {
    const [tipoLocal, setTipoLocal] = useState(config.settings?.impresora_tipo || '80mm');
    // Nuevo estado para la conexión
    const [conexionLocal, setConexionLocal] = useState(config.settings?.conexion_impresora || 'sistema');
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        setTipoLocal(config.settings?.impresora_tipo || '80mm');
        setConexionLocal(config.settings?.conexion_impresora || 'sistema');
    }, [config.settings?.impresora_tipo, config.settings?.conexion_impresora]);

    // Validar si hubo cambios en cualquiera de los dos ajustes
    const hayCambios = tipoLocal !== (config.settings?.impresora_tipo || '80mm') || 
                       conexionLocal !== (config.settings?.conexion_impresora || 'sistema');

    const handleGuardar = async () => {
        if (!hayCambios) return;
        setGuardando(true);
        try {
            // Guardamos ambos valores en la base de datos
            await updateConfig({ 
                impresora_tipo: tipoLocal,
                conexion_impresora: conexionLocal 
            });
            message.success('Ajustes de impresión guardados correctamente');
        } catch (err) {
            message.error(err.response?.data?.error || 'Error al guardar');
        } finally {
            setGuardando(false);
        }
    };

    const OptionCard = ({ value, currentValue, setter, icon, title, desc }) => {
        const isSelected = currentValue === value;
        return (
            <Card 
                hoverable onClick={() => setter(value)}
                style={{ 
                    borderColor: isSelected ? '#22BABB' : '#e2e8f0', 
                    backgroundColor: isSelected ? '#f0fdfa' : '#fff',
                    borderWidth: isSelected ? 2 : 1,
                    height: '100%'
                }}
                styles={{ body: { padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' } }}
            >
                <div style={{ fontSize: '32px', color: isSelected ? '#22BABB' : '#64748b', marginBottom: '8px' }}>{icon}</div>
                <Text strong style={{ color: isSelected ? '#0f172a' : '#475569', fontSize: '14px' }}>{title}</Text>
                <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>{desc}</Text>
            </Card>
        );
    };

    return (
        <div className="fade-in" style={{ maxWidth: 800 }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={3} style={{ color: '#1e293b', marginBottom: 8 }}><PrinterOutlined style={{ color: '#22BABB', marginRight: 10 }} />Ajustes de Facturación</Title>
                <Text type="secondary" style={{ fontSize: '15px' }}>Personaliza el formato de tus recibos y el método de conexión con el hardware.</Text>
            </div>

            <Card bordered={false} style={{ borderRadius: 16, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    
                    {/* Tarjetas de Conexión de Hardware (NUEVO) */}
                    <div>
                        <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 12, color: '#334155' }}>Método de Conexión de Impresora</Text>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <OptionCard 
                                    value="sistema" currentValue={conexionLocal} setter={setConexionLocal} 
                                    icon={<DesktopOutlined />} title="Sistema (USB/Red/Wi-Fi)" 
                                    desc="Usa el cuadro de impresión nativo del navegador (Recomendado para PC)." />
                            </Col>
                            <Col xs={24} md={12}>
                                <OptionCard 
                                    value="bluetooth" currentValue={conexionLocal} setter={setConexionLocal} 
                                    icon={<ThunderboltOutlined />} 
                                    title="Bluetooth Directo (ESC/POS)" 
                                    desc="Conexión vía Web Bluetooth. ⚠️ Compatible únicamente con Google Chrome (PC y Tablets Android)." />
                            </Col>
                        </Row>
                    </div>

                    <Divider style={{ margin: '0' }} />

                    {/* Tarjetas de Formato */}
                    <div>
                        <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 12, color: '#334155' }}>Formato del Documento</Text>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}>
                                <OptionCard value="57mm" currentValue={tipoLocal} setter={setTipoLocal} icon={<ContainerOutlined />} title="Térmica 57mm" desc="Datáfonos y mini impresoras." />
                            </Col>
                            <Col xs={24} md={8}>
                                <OptionCard value="80mm" currentValue={tipoLocal} setter={setTipoLocal} icon={<ProfileOutlined />} title="Térmica 80mm" desc="Formato estándar Retail." />
                            </Col>
                            <Col xs={24} md={8}>
                                <OptionCard value="carta_mitad" currentValue={tipoLocal} setter={setTipoLocal} icon={<FileTextOutlined />} title="PDF / Carta" desc="Documentos formales." />
                            </Col>
                        </Row>
                    </div>

                    {hayCambios && (
                        <Alert message={<span style={{ fontWeight: 600 }}>Cambios sin guardar</span>} type="warning" showIcon style={{ borderRadius: 8, backgroundColor: '#fffbeb', borderColor: '#fef08a' }} />
                    )}

                    <Divider style={{ margin: '0' }} />

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                        <Text type="secondary" style={{ fontSize: '13px' }}><InfoCircleOutlined style={{ marginRight: 6 }}/>Los cambios aplicarán en la próxima venta.</Text>
                        <Button type="primary" size="large" icon={<SaveOutlined />} onClick={handleGuardar} loading={guardando} disabled={!hayCambios || configLoading} style={{ backgroundColor: hayCambios ? '#22BABB' : '#cbd5e1', borderColor: 'transparent', borderRadius: 8, fontWeight: 600 }}>
                            Guardar Ajustes
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default SeccionImpresion;